import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const config = document.querySelector('[data-vv-cart-config]');
if (config) {
  const { CartClock } = await import(config.dataset.clockUrl);
  const root = config.dataset.root;
  const activeRequests = new Set();
  let reconciling = null;
  let revision = 0;
  let clock;
  let storage;
  try { storage = window.localStorage; } catch { storage = { getItem() { throw new Error('Storage unavailable'); } }; }

  const render = () => {
    if (!clock) return;
    const state = clock.state();
    document.querySelectorAll('[data-vv-cart-timer]').forEach(timer => {
      timer.hidden = state.phase === 'unavailable' || state.phase === 'idle';
      const display = timer.querySelector('[data-vv-cart-time]');
      const time = `${String(Math.floor(state.seconds / 60)).padStart(2, '0')}:${String(state.seconds % 60).padStart(2, '0')}`;
      if (display && display.textContent !== time) display.textContent = time;
      const error = timer.querySelector('[data-vv-cart-error]');
      if (error) error.hidden = state.phase !== 'error';
      timer.toggleAttribute('aria-busy', state.phase === 'clearing');
    });
    document.querySelectorAll('[data-vv-cart-expired]').forEach(note => { note.hidden = !state.expired; });
    document.querySelectorAll('vv-cart-checkout').forEach(guard => guard.update?.());
  };

  const clearExpired = async deadline => {
    const clear = async () => {
      await Promise.allSettled(Array.from(activeRequests));
      // Recheck after waiting: a different tab may have cleared the old cart.
      if (clock.deadline() !== deadline) return false;
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(new CartLinesUpdateEvent({ action: 'remove', context: 'cart', lines: [], promise: deferred.promise }));
      try {
        const sections = [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), el => el.dataset.sectionId))].slice(0, 5);
        const response = await fetch(`${root}cart/clear.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ sections, sections_url: location.pathname }),
        });
        if (!response.ok) throw new Error('Cart clear failed');
        const cart = await response.json();
        if (cart.item_count !== 0) throw new Error('Cart was not cleared');
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { sections: cart.sections, items: cart.items, itemCount: 0, source: 'vv-cart-expiry' } });
        return true;
      } catch (error) {
        deferred.reject(error);
        throw error;
      }
    };
    return navigator.locks ? navigator.locks.request('mart-supply:cart-expiry', clear) : clear();
  };

  clock = new CartClock({ storage, clear: clearExpired, changed: render });
  const reconcile = (notify = false) => {
    if (reconciling) return reconciling;
    const currentRevision = revision;
    reconciling = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(response => { if (!response.ok) throw new Error('Cart unavailable'); return response.json(); })
      .then(async cart => {
        if (clock.pending) await clock.pending;
        if (revision !== currentRevision) return;
        await clock.sync(cart.item_count);
        if (notify && revision === currentRevision) {
          document.dispatchEvent(new CartLinesUpdateEvent({
            action: 'update', context: 'cart', lines: [],
            promise: Promise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, itemCount: cart.item_count, source: 'vv-cart-reconcile' } }),
          }));
        }
      })
      .catch(() => { /* Keep the current deadline; an offline request must not reset it. */ })
      .finally(() => { reconciling = null; render(); });
    render();
    return reconciling;
  };

  class VVCartCheckout extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.addEventListener('change', () => this.update(), { signal: this.abort.signal });
      this.update();
    }
    disconnectedCallback() { this.abort?.abort(); }
    update() {
      const button = this.querySelector('[name="checkout"]');
      const checkbox = this.querySelector('[data-vv-cart-consent]');
      if (button) button.disabled = !checkbox?.checked || clock.state().blocked || !clock.count || !!reconciling;
    }
  }
  if (!customElements.get('vv-cart-checkout')) customElements.define('vv-cart-checkout', VVCartCheckout);

  // The native event promise includes the pending Ajax mutation and its cart result.
  document.addEventListener(StandardEvents.cartLinesUpdate, event => {
    revision++;
    const request = event.promise;
    if (!request) return;
    activeRequests.add(request);
    request.then(({ cart, detail }) => {
      if (!['vv-cart-expiry', 'vv-cart-reconcile'].includes(detail?.source)) clock.sync(cart?.totalQuantity ?? detail?.itemCount ?? 0);
    }).catch(() => {}).finally(() => { activeRequests.delete(request); render(); });
  });
  // Native controls, keyboard submits, quick-add and cart checkout are all guarded.
  const guardAction = event => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const checkout = event.type === 'submit'
      ? event.submitter?.matches('[name="checkout"]') || target.matches('form.cart-form')
      : target.closest('button[name="checkout"], a[href$="/checkout"]');
    const cartAction = checkout || (event.type === 'submit'
      ? target.closest('product-form-component')
      : target.closest('add-to-cart-component button, quantity-selector-component button, cart-quantity-selector-component button, cart-quantity-selector-component input, cart-items-component button'));
    if (!cartAction) return;
    if (clock.state().blocked || (checkout && reconciling)) {
      event.preventDefault(); event.stopImmediatePropagation();
      void clock.tick();
      return;
    }
    if (checkout) {
      const guard = event.type === 'submit'
        ? document.querySelector('vv-cart-checkout')
        : target.closest('vv-cart-checkout');
      const checkbox = guard?.querySelector('[data-vv-cart-consent]');
      if (!checkbox?.checked) {
        event.preventDefault(); event.stopImmediatePropagation(); checkbox?.reportValidity();
      }
    }
  };
  document.addEventListener('click', guardAction, true);
  document.addEventListener('submit', guardAction, true);
  document.addEventListener('change', guardAction, true);
  window.addEventListener('storage', event => {
    if (event.key !== clock.key) return;
    revision++;
    // Wait for an older read or an expiry lock before reading the shared cart.
    const previous = reconciling;
    if (previous) void previous.finally(() => reconcile(true));
    else void reconcile(true);
  });
  window.addEventListener('pageshow', () => { void reconcile(); });
  window.addEventListener('online', () => { void clock.tick(); void reconcile(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) { void clock.tick(); void reconcile(); } });
  // Morphing can preserve a checked input while replacing the checkout button.
  const observer = new MutationObserver(() => render());
  observer.observe(document.body, { childList: true, subtree: true });
  setInterval(() => { if (!config.hasAttribute('data-design-mode')) void clock.tick(); }, 1000);
  if (!config.hasAttribute('data-design-mode')) void reconcile();
}
